import { filter, flatMap, groupBy, includes, mapValues, size } from 'es-toolkit/compat';
import { ExperimentResult } from 'interfaces/experimentResults';
import { FormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { FeatureMetadata, HeatmapType, ParsedResults } from './featureMetadata';
import { parseFeatures } from './parseFeatures';
import {
  classificationPredictionClassIdName,
  HeatmapParsingCache,
  labelColumnName,
  ParsedExperimentResultHeatmaps,
  parseHeatmaps,
} from './parseHeatmaps';

/** For published heatmaps with the same display name, we want to keep the vector heatmap over the raster heatmap */
export const filterRedundantRasterHeatmaps = (heatmaps: FeatureMetadata[]): FeatureMetadata[] => {
  const heatmapsResultsSidebarGrouping = groupBy(
    heatmaps,
    (heatmap) =>
      `${heatmap.resultType || heatmap.flowClassName}-${heatmap.orchestrationId}-${heatmap.displayName}-${
        heatmap?.registeredFromStainTypeIndex ?? ''
      }-${
        heatmap.columnName &&
        heatmap.columnName !== labelColumnName &&
        heatmap.columnName !== classificationPredictionClassIdName
          ? heatmap.columnName
          : ''
      }`
  );

  return flatMap(heatmapsResultsSidebarGrouping, (heatmapsGroup) => {
    const vectorHeatmaps = filter(heatmapsGroup, ({ heatmapType }) =>
      includes([HeatmapType.Parquet, HeatmapType.Pmt], heatmapType)
    );
    return size(vectorHeatmaps) > 0 ? vectorHeatmaps : heatmapsGroup;
  });
};

export const parseSlideExperimentResults = (
  slideResultsRaw: ExperimentResult[],
  formatBracketsOptions: Omit<FormatBracketsOptions, 'orchestrationId'>,
  isMultiStain: boolean,
  skipVectorHeatmaps = false,
  parsingCache?: HeatmapParsingCache,
  parsedResultHeatmaps?: ParsedExperimentResultHeatmaps[]
): ParsedResults => {
  const { publishedFeatures, internalFeatures, deletedFeatures } = parseFeatures(slideResultsRaw);

  const { publishedHeatmaps, publishableHeatmaps, internalHeatmaps, deletedHeatmaps } = parseHeatmaps(
    slideResultsRaw,
    formatBracketsOptions,
    isMultiStain,
    skipVectorHeatmaps,
    parsingCache,
    parsedResultHeatmaps
  );

  const results: ParsedResults = {
    heatmaps: {
      publishedResults: filterRedundantRasterHeatmaps(publishedHeatmaps),
      internalResults: mapValues(publishableHeatmaps, filterRedundantRasterHeatmaps),
      // TODO: note: no UI exists for deleted heatmaps yet, even if the user can restore them,
      // except for secondary analysis (which are nested in the primary result)
      deletedResults: deletedHeatmaps,
    },
    features: {
      publishedResults: publishedFeatures,
      internalResults: internalFeatures,
      // TODO: note: no UI exists for deleted heatmaps yet, even if the user can restore them,
      // except for secondary analysis (which are nested in the primary result)
      deletedResults: deletedFeatures,
    },
    internalHeatmaps,
  };

  return results;
};
